import React from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundaryFallback from './ErrorBoundaryFallback';

const withRouter = (Component) => {
    return (props) => {
        const navigate = useNavigate();
        return <Component {...props} navigate={navigate} />;
    };
};

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
        this.lastReportTime = 0;
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        if (process.env.NODE_ENV === 'development') {
            console.log('📧 Sending error report...');
        }
        console.error('🔥 ErrorBoundary caught error:', error, errorInfo);
        this.logErrorToService(error, errorInfo);
        this.sendAutoEmailReport(error, errorInfo);
    }

    logErrorToService = async (error, errorInfo) => {
        try {
            if (process.env.NODE_ENV === 'production') {
                const errorData = {
                    error: error.toString(),
                    stack: error.stack,
                    componentStack: errorInfo.componentStack,
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    timestamp: new Date().toISOString(),
                    version: process.env.REACT_APP_VERSION || '1.0.0'
                };
                await this.sendToAnalytics(errorData);
            }
        } catch (loggingError) {
            console.warn('Error logging failed:', loggingError);
        }
    };

    sendAutoEmailReport = async (error, errorInfo) => {
        if (process.env.NODE_ENV === 'development') {
            console.log('📧 Sending error report...');
        }

        try {
            const response = await fetch('/api/error-report', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    error: error.toString(),
                    stack: error.stack,
                    componentStack: errorInfo?.componentStack,
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    version: process.env.REACT_APP_VERSION || '1.0.0',
                    type: 'automatic'
                }),
            });

            if (process.env.NODE_ENV === 'development') {
                console.log('📧 Error report response:', response.status);
            }
        } catch (err) {
            console.error('❌ Failed to send error report:', err);
        }
    };

    sendToAnalytics = async (errorData) => {
        try {
            if (window.Sentry) {
                window.Sentry.captureException(errorData.error, { extra: errorData });
            }
            await fetch('/api/log-error', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(errorData),
            });
        } catch (e) {
            console.warn('Analytics error:', e);
        }
    };

    handleReset = () => {
        this.setState({ hasError: false });
        this.props.onReset?.();
    };

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            return (
                <ErrorBoundaryFallback
                    onReset={this.handleReset}
                    onReload={this.handleReload}
                    onGoHome={this.handleGoHome}
                />
            );
        }
        return this.props.children;
    }
}

ErrorBoundary.defaultProps = { onReset: null };

export default withRouter(ErrorBoundary);